import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import './AssistantManagement.css';

export default function AssistantManagement() {
    const [assistant, setAssistant] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [newAssistant, setNewAssistant] = useState({
        full_name: '',
        username: '',
        password: ''
    });
    const [editFormData, setEditFormData] = useState({
        full_name: '',
        username: '',
        password: ''
    });
    const [confirmDelete, setConfirmDelete] = useState(false);
    
    const navigate = useNavigate();
    axios.defaults.withCredentials = true;
    const API_URL = process.env.REACT_APP_API_URL;

    // Fetch assistant data
    const fetchAssistant = async () => {
        try {
            setLoading(true);
                        
            const response = await axios.get(`${API_URL}assistants`, {
                headers: { 'Accept': 'application/json' }
            });

            if (response.data.success) {
                setAssistant(response.data.data);
            } else {
                setAssistant(null);
            }
            setError('');
        } catch (error) {
            console.error('Error fetching assistant:', error);
            if (error.response?.status === 401) {
                navigate('/login');
            } else {
                setError(`Error: ${error.response?.data?.message || error.message}`);
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAssistant();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Handle form input changes
    const handleChange = (e) => {
        const { name, value } = e.target;
        setNewAssistant(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleEditChange = (e) => {
        const { name, value } = e.target;
        setEditFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Create assistant
    const handleCreateSubmit = async (e) => {
        e.preventDefault();
        setError('');

        try {
                        const response = await axios.post(`${API_URL}assistants`, newAssistant, {
                headers: { 'Accept': 'application/json' }
            });

            if (response.data.success) {
                setAssistant(response.data.data);
                setShowCreateModal(false);
                setNewAssistant({
                    full_name: '',
                    username: '',
                    password: ''
                });
                setError('');
            }
        } catch (error) {
            console.error('Error creating assistant:', error);
            setError(`Error: ${error.response?.data?.message || error.message}`);
        }
    };

    // Update assistant
    const handleEditSubmit = async (e) => {
        e.preventDefault();
        setError('');

        try {
                        // Only send fields that have values
            const updateData = {};
            if (editFormData.full_name) updateData.full_name = editFormData.full_name;
            if (editFormData.username) updateData.username = editFormData.username;
            if (editFormData.password) updateData.password = editFormData.password;

            const response = await axios.put(`${API_URL}assistants/${assistant.id}`, updateData, {
                headers: { 'Accept': 'application/json' }
            });

            if (response.data.success) {
                setAssistant(response.data.data);
                setShowEditModal(false);
                setEditFormData({
                    full_name: '',
                    username: '',
                    password: ''
                });
                setError('');
            }
        } catch (error) {
            console.error('Error updating assistant:', error);
            setError(`Error: ${error.response?.data?.message || error.message}`);
        }
    };

    // Delete assistant
    const handleDelete = async () => {
        setError('');

        try {
                        const response = await axios.delete(`${API_URL}assistants/${assistant.id}`, {
                headers: { 'Accept': 'application/json' }
            });

            if (response.data.success) {
                setAssistant(null);
                setConfirmDelete(false);
                setError('');
            }
        } catch (error) {
            console.error('Error deleting assistant:', error);
            setError(`Error: ${error.response?.data?.message || error.message}`);
        }
    };

    // Open edit modal with current data
    const openEditModal = () => {
        setEditFormData({
            full_name: assistant?.full_name || '',
            username: assistant?.user?.name || '',
            email: assistant?.user?.email || '',
            password: ''
        });
        setShowEditModal(true);
    };

    if (loading) {
        return (
            <div className="container-fluid">
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
        <div className="patient-list-container">
            <div className="container py-4">
                <div className="row justify-content-center">
                    <div className="col-md-12">
                        <div className="card patient-list-card">
                            <div className="card-header patient-list-header d-flex justify-content-between align-items-center">
                                <h4>
                                    <i className="fas fa-user-tie me-2"></i>
                                    Assistant Management
                                </h4>
                                {!assistant && (
                                    <button 
                                        className="btn create-patient-btn"
                                        onClick={() => setShowCreateModal(true)}
                                    >
                                        <i className="fas fa-plus me-2"></i>
                                        Create Assistant
                                    </button>
                                )}
                            </div>
                            <div className="card-body patient-list-body">
                                {error && (
                                    <div className="alert alert-danger" role="alert">
                                        {error}
                                    </div>
                                )}

                                {assistant ? (
                                    <div className="assistant-info-section">
                                        <div className="row">
                                            <div className="col-md-6">
                                                <div className="info-group mb-3">
                                                    <label className="form-label fw-bold">Full Name:</label>
                                                    <p className="mb-0">{assistant.full_name}</p>
                                                </div>
                                                <div className="info-group mb-3">
                                                    <label className="form-label fw-bold">Username:</label>
                                                    <p className="mb-0">{assistant.user?.name}</p>
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="info-group mb-3">
                                                    <label className="form-label fw-bold">Created:</label>
                                                    <p className="mb-0">
                                                        {new Date(assistant.created_at).toLocaleDateString()}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="d-flex gap-2 mt-4">
                                            <button 
                                                className="btn btn-outline-primary"
                                                onClick={openEditModal}
                                            >
                                                <i className="fas fa-edit me-2"></i>
                                                Edit Assistant
                                            </button>
                                            <button 
                                                className="btn btn-outline-danger"
                                                onClick={() => setConfirmDelete(true)}
                                            >
                                                <i className="fas fa-trash me-2"></i>
                                                Delete Assistant
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="empty-state text-center py-5">
                                        <i className="fas fa-user-tie fa-3x text-muted mb-3"></i>
                                        <h5>No Assistant Found</h5>
                                        <p className="text-muted mb-4">
                                            You don't have an assistant yet. Create one to help manage your practice.
                                        </p>
                                        <button 
                                            className="btn create-patient-btn"
                                            onClick={() => setShowCreateModal(true)}
                                        >
                                            <i className="fas fa-plus me-2"></i>
                                            Create Assistant
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        {/* Create Assistant Modal */}
        {showCreateModal && (
            <div className="modal-container">
                <div className="modal-backdrop fade show" onClick={() => setShowCreateModal(false)}></div>
                <div className="modal fade show d-block" tabIndex="-1" style={{ zIndex: 1055 }}>
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    <i className="fas fa-user-plus me-2"></i>
                                    Create New Assistant
                                </h5>
                                <button 
                                    type="button" 
                                    className="btn-close" 
                                    onClick={() => setShowCreateModal(false)}
                                ></button>
                            </div>
                            <form onSubmit={handleCreateSubmit}>
                                <div className="modal-body">
                                    <div className="row">
                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <label htmlFor="full_name" className="form-label">Full Name *</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    id="full_name"
                                                    name="full_name"
                                                    value={newAssistant.full_name}
                                                    onChange={handleChange}
                                                    required
                                                />
                                            </div>
                                            <div className="mb-3">
                                                <label htmlFor="username" className="form-label">Username * (min 8 characters)</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    id="username"
                                                    name="username"
                                                    value={newAssistant.username}
                                                    onChange={handleChange}
                                                    required
                                                    minLength="8"
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <label htmlFor="password" className="form-label">Password * (min 8 characters)</label>
                                                <input
                                                    type="password"
                                                    className="form-control"
                                                    id="password"
                                                    name="password"
                                                    value={newAssistant.password}
                                                    onChange={handleChange}
                                                    required
                                                    minLength="8"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button 
                                        type="button" 
                                        className="btn btn-secondary" 
                                        onClick={() => setShowCreateModal(false)}
                                    >
                                        Cancel
                                    </button>
                                    <button 
                                        type="submit" 
                                        className="btn btn-primary"
                                        disabled={loading}
                                    >
                                        {loading ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                                Creating...
                                            </>
                                        ) : (
                                            <>
                                                <i className="fas fa-save me-2"></i>
                                                Create Assistant
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        )}

        {/* Edit Assistant Modal */}
        {showEditModal && (
            <div className="modal-container">
                <div className="modal-backdrop fade show" onClick={() => setShowEditModal(false)}></div>
                <div className="modal fade show d-block" tabIndex="-1" style={{ zIndex: 1055 }}>
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    <i className="fas fa-edit me-2"></i>
                                    Edit Assistant
                                </h5>
                                <button 
                                    type="button" 
                                    className="btn-close" 
                                    onClick={() => setShowEditModal(false)}
                                ></button>
                            </div>
                            <form onSubmit={handleEditSubmit}>
                                <div className="modal-body">
                                    <div className="row">
                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <label htmlFor="edit_full_name" className="form-label">Full Name</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    id="edit_full_name"
                                                    name="full_name"
                                                    placeholder={assistant?.full_name}
                                                    value={editFormData.full_name}
                                                    onChange={handleEditChange}
                                                />
                                            </div>
                                            <div className="mb-3">
                                                <label htmlFor="edit_username" className="form-label">Username</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    id="edit_username"
                                                    name="username"
                                                    placeholder={assistant?.user?.name}
                                                    value={editFormData.username}
                                                    onChange={handleEditChange}
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <label htmlFor="edit_password" className="form-label">Password (leave empty to keep current)</label>
                                                <input
                                                    type="password"
                                                    className="form-control"
                                                    id="edit_password"
                                                    name="password"
                                                    value={editFormData.password}
                                                    onChange={handleEditChange}
                                                    minLength="8"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button 
                                        type="button" 
                                        className="btn btn-secondary" 
                                        onClick={() => setShowEditModal(false)}
                                    >
                                        Cancel
                                    </button>
                                    <button 
                                        type="submit" 
                                        className="btn btn-primary"
                                        disabled={loading}
                                    >
                                        {loading ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                                Updating...
                                            </>
                                        ) : (
                                            <>
                                                <i className="fas fa-save me-2"></i>
                                                Update Assistant
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        )}

        {/* Delete Confirmation Modal */}
        {confirmDelete && (
            <div className="modal-container">
                <div className="modal-backdrop fade show" onClick={() => setConfirmDelete(false)}></div>
                <div className="modal fade show d-block" tabIndex="-1" style={{ zIndex: 1055 }}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title text-danger">
                                    <i className="fas fa-exclamation-triangle me-2"></i>
                                    Confirm Delete
                                </h5>
                                <button 
                                    type="button" 
                                    className="btn-close" 
                                    onClick={() => setConfirmDelete(false)}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <p>Are you sure you want to delete this assistant?</p>
                                <p className="text-muted small">
                                    <strong>Warning:</strong> This action cannot be undone. The assistant's user account will also be permanently deleted.
                                </p>
                            </div>
                            <div className="modal-footer">
                                <button 
                                    type="button" 
                                    className="btn btn-secondary" 
                                    onClick={() => setConfirmDelete(false)}
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="button" 
                                    className="btn btn-danger"
                                    onClick={handleDelete}
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                            Deleting...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fas fa-trash me-2"></i>
                                            Delete Assistant
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        )}
        </>
    );
}
